/**
 * TranscriptSegment Component
 *
 * Displays an individual transcript segment as a card with speaker avatar,
 * name, annotation badges with tooltips, and message text. Matches Magic Patterns
 * design with colored backgrounds based on speaker identity, highlighted key phrases,
 * inline chat functionality, and comment count indicators.
 */

import { useMemo, useState, useCallback } from "react";
import { cn } from "@/lib/utils";
import { MessageCircle, ThumbsUp, AlertTriangle } from "lucide-react";
import type { WordTimestamp, SentimentLabel } from "@/types/transcript";
import type { SegmentAnnotationSummary } from "@/types/annotation";
import {
  ANNOTATION_TYPE_COLORS,
  ANNOTATION_TYPE_LABELS,
} from "@/types/annotation";
import { InlineChat } from "@/components/chat/InlineChat";

interface TranscriptSegmentProps {
  segment: {
    id: string;
    speaker_label: string;
    text: string;
    start_time_ms: number;
    end_time_ms: number;
    word_timestamps?: WordTimestamp[] | null;
    sequence_order?: number;
    sentiment_label?: SentimentLabel | null;
    sentiment_confidence?: number | null;
    emotion_data?: Record<string, unknown> | null;
  };
  isActive: boolean;
  speakerColor: string;
  /** Display name for the speaker (custom_name or display_label from speakers list) */
  speakerDisplayName?: string;
  currentTimeMs?: number;
  onClick: () => void;
  onSentenceClick?: (startTimeMs: number) => void;
  /** Whether to show block labels (e.g., "Block 1") */
  showBlockLabel?: boolean;
  /** Annotations for this segment (optional) */
  annotations?: SegmentAnnotationSummary[];
  /** Callback when comment icon is clicked (for external chat drawer) */
  onCommentClick?: (segmentId: string) => void;
  /** Number of messages in chat for this segment (for badge display) */
  chatMessageCount?: number;
  /** Search query for highlighting matching text */
  searchQuery?: string;
  /** Whether inline chat is open for this segment */
  isChatOpen?: boolean;
  /** Callback to toggle inline chat */
  onChatToggle?: (segmentId: string | null) => void;
  /** Transcript ID for inline chat */
  transcriptId?: string;
  /** Callback when chat message count changes */
  onMessageCountChange?: (segmentId: string, count: number) => void;
  /** Whether speakers have been confirmed - enables chat when true */
  speakersConfirmed?: boolean;
  /** Type of key moment this segment represents (null = not a key moment) */
  keyMomentType?: "tension" | "positive" | null;
}

/**
 * Sentence with time range derived from word timestamps
 */
interface SentenceWithTiming {
  text: string;
  startMs: number;
  endMs: number;
}

/**
 * Format milliseconds to timestamp string (H:MM:SS)
 */
function formatTimestamp(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return `${hours}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
}

/**
 * Split text into sentences and map them to time ranges using word timestamps.
 * If word timestamps are not available, returns the full text as a single sentence.
 */
function parseSentencesWithTiming(
  text: string,
  wordTimestamps: WordTimestamp[] | null | undefined,
  segmentStartMs: number,
  segmentEndMs: number
): SentenceWithTiming[] {
  // If no word timestamps, return full text as single sentence
  if (!wordTimestamps || wordTimestamps.length === 0) {
    return [{ text, startMs: segmentStartMs, endMs: segmentEndMs }];
  }

  // Split text by sentence-ending punctuation while keeping the punctuation
  const sentenceRegex = /[^.!?]*[.!?]+\s*/g;
  const matches = text.match(sentenceRegex);

  // If no sentence boundaries found, return full text
  if (!matches || matches.length === 0) {
    return [{ text, startMs: segmentStartMs, endMs: segmentEndMs }];
  }

  // Handle any remaining text after the last sentence
  const matchedText = matches.join("");
  const remainder = text.slice(matchedText.length).trim();
  const sentences = remainder ? [...matches, remainder] : matches;

  // Map sentences to word indices
  const result: SentenceWithTiming[] = [];
  let wordIndex = 0;

  for (const sentence of sentences) {
    const trimmedSentence = sentence.trim();
    if (!trimmedSentence) continue;

    // Count words in this sentence (simple split by whitespace)
    const sentenceWords = trimmedSentence.split(/\s+/);
    const wordCount = sentenceWords.length;

    // Get timing from word timestamps
    const startWordIndex = wordIndex;
    const endWordIndex = Math.min(
      wordIndex + wordCount - 1,
      wordTimestamps.length - 1
    );

    const startMs = wordTimestamps[startWordIndex]?.start ?? segmentStartMs;
    const endMs = wordTimestamps[endWordIndex]?.end ?? segmentEndMs;

    result.push({
      text: trimmedSentence,
      startMs,
      endMs,
    });

    wordIndex += wordCount;
  }

  // If no sentences were parsed, return full text
  if (result.length === 0) {
    return [{ text, startMs: segmentStartMs, endMs: segmentEndMs }];
  }

  return result;
}

/**
 * Speaker-specific avatar colors
 * Speaker A = blue, Speaker B = purple
 */
const SPEAKER_AVATAR_COLORS: Record<string, string> = {
  A: "bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400",
  B: "bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-400",
  C: "bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400",
  D: "bg-amber-100 dark:bg-amber-900 text-amber-600 dark:text-amber-400",
};

/**
 * Speaker name colors - matching avatar colors
 */
const SPEAKER_NAME_COLORS: Record<string, string> = {
  A: "text-blue-600 dark:text-blue-400",
  B: "text-purple-600 dark:text-purple-400",
  C: "text-green-600 dark:text-green-400",
  D: "text-amber-600 dark:text-amber-400",
};

/**
 * Determine if an annotation is positive (strength) or negative (growth area)
 */
function isPositiveAnnotation(annotationType: string): boolean {
  return ["pattern", "valid_feeling", "positive", "insight"].includes(
    annotationType
  );
}

export function TranscriptSegment({
  segment,
  isActive,
  speakerColor: _speakerColor,
  speakerDisplayName,
  currentTimeMs = 0,
  onClick,
  onSentenceClick,
  showBlockLabel = false,
  annotations,
  onCommentClick,
  chatMessageCount,
  searchQuery = "",
  isChatOpen = false,
  onChatToggle,
  transcriptId,
  onMessageCountChange,
  speakersConfirmed = true,
  keyMomentType = null,
}: TranscriptSegmentProps) {
  // Track hover state for showing timestamp
  const [isHovered, setIsHovered] = useState(false);
  // Track hover state for sentiment tooltip
  const [showSentimentTip, setShowSentimentTip] = useState(false);
  // Track which annotation tooltip is being shown
  const [hoveredAnnotationId, setHoveredAnnotationId] = useState<string | null>(
    null
  );

  // Callback to report message count changes
  const handleMessageCountChange = useCallback(
    (count: number) => {
      onMessageCountChange?.(segment.id, count);
    },
    [segment.id, onMessageCountChange]
  );

  // Parse sentences with timing information
  const sentences = useMemo(
    () =>
      parseSentencesWithTiming(
        segment.text,
        segment.word_timestamps,
        segment.start_time_ms,
        segment.end_time_ms
      ),
    [
      segment.text,
      segment.word_timestamps,
      segment.start_time_ms,
      segment.end_time_ms,
    ]
  );

  // Find active sentence based on current time (only when segment is active)
  const activeSentenceIndex = useMemo(() => {
    if (!isActive) return -1;

    // First, check if we're exactly within a sentence's time range
    for (let i = 0; i < sentences.length; i++) {
      if (
        currentTimeMs >= sentences[i].startMs &&
        currentTimeMs < sentences[i].endMs
      ) {
        return i;
      }
    }

    // If between sentences (in a gap), find the most recently ended sentence
    // This keeps the highlight on the previous sentence instead of jumping
    for (let i = sentences.length - 1; i >= 0; i--) {
      if (currentTimeMs >= sentences[i].endMs) {
        // We're past this sentence - highlight it if we haven't reached the next one yet
        if (
          i < sentences.length - 1 &&
          currentTimeMs < sentences[i + 1].startMs
        ) {
          return i;
        }
        // If this is the last sentence and we're past it but still in segment
        if (i === sentences.length - 1 && currentTimeMs < segment.end_time_ms) {
          return i;
        }
      }
    }

    // If before the first sentence starts but segment is active
    if (
      currentTimeMs < sentences[0]?.startMs &&
      currentTimeMs >= segment.start_time_ms
    ) {
      return 0;
    }

    return -1;
  }, [
    isActive,
    sentences,
    currentTimeMs,
    segment.start_time_ms,
    segment.end_time_ms,
  ]);

  // Handle sentence click
  const handleSentenceClick = (
    e: React.MouseEvent,
    sentence: SentenceWithTiming
  ) => {
    e.stopPropagation();
    if (onSentenceClick) {
      onSentenceClick(sentence.startMs);
    } else {
      onClick();
    }
  };

  // Block label (1-indexed for display)
  const blockLabel =
    showBlockLabel && segment.sequence_order !== undefined
      ? `Block ${segment.sequence_order + 1}`
      : null;

  // Get speaker letter (A, B, C, D) for consistent coloring
  const speakerLetter = segment.speaker_label?.charAt(0)?.toUpperCase() || "A";
  const avatarColor =
    SPEAKER_AVATAR_COLORS[speakerLetter] || "bg-slate-200 text-slate-600";
  const nameColor = SPEAKER_NAME_COLORS[speakerLetter] || "text-foreground";

  // Get the first letter of the display name for the avatar
  const avatarLetter =
    (speakerDisplayName || segment.speaker_label)?.charAt(0)?.toUpperCase() ||
    "?";

  // Highlight search matches in text
  const highlightText = (text: string) => {
    if (!searchQuery.trim()) return text;

    const regex = new RegExp(
      `(${searchQuery.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
      "gi"
    );
    const parts = text.split(regex);

    return parts.map((part, i) =>
      regex.test(part) ? (
        <span
          key={i}
          className="bg-yellow-200 dark:bg-yellow-800 rounded px-0.5"
        >
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  // Check if this segment matches the search
  const matchesSearch =
    searchQuery.trim() &&
    segment.text.toLowerCase().includes(searchQuery.toLowerCase());

  // Check if there are comments
  const hasComments = chatMessageCount && chatMessageCount > 0;

  return (
    <div
      className={cn(
        "group relative flex gap-4 p-4 rounded-xl transition-all",
        isActive
          ? "bg-blue-50/30 dark:bg-blue-950/30 ring-1 ring-blue-100/50 dark:ring-blue-900/50"
          : keyMomentType === "tension"
            ? "bg-red-50/40 dark:bg-red-950/40 hover:bg-red-50/60 dark:hover:bg-red-950/60"
            : keyMomentType === "positive"
              ? "bg-green-50/40 dark:bg-green-950/40 hover:bg-green-50/60 dark:hover:bg-green-950/60"
              : "hover:bg-muted/30",
        matchesSearch &&
          "bg-yellow-50 dark:bg-yellow-950 ring-1 ring-yellow-200 dark:ring-yellow-800"
      )}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-segment-id={segment.id}
      data-start-time={segment.start_time_ms}
      data-block={
        segment.sequence_order !== undefined
          ? segment.sequence_order + 1
          : undefined
      }
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        // Don't handle if the event originated from an interactive element inside
        const target = e.target as HTMLElement;
        const tagName = target.tagName.toUpperCase();
        const isInteractiveElement =
          tagName === "INPUT" ||
          tagName === "TEXTAREA" ||
          tagName === "SELECT" ||
          tagName === "BUTTON" ||
          target.isContentEditable;

        if (isInteractiveElement) return;

        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
    >
      {/* Speaker Avatar */}
      <div className="flex-shrink-0 mt-1">
        <div
          className={cn(
            "w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold",
            avatarColor
          )}
        >
          {avatarLetter}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {/* Block Label */}
            {blockLabel && (
              <span className="text-xs font-semibold text-muted-foreground">
                {blockLabel}
              </span>
            )}

            {/* Speaker Label */}
            {(speakerDisplayName || segment.speaker_label) && (
              <span
                className={cn(
                  "text-xs font-bold uppercase tracking-wider",
                  nameColor
                )}
              >
                {speakerDisplayName || segment.speaker_label}
              </span>
            )}

            {/* Sentiment Indicator Dot with Tooltip */}
            {segment.sentiment_label && (
              <div
                className="relative inline-flex"
                onMouseEnter={() => setShowSentimentTip(true)}
                onMouseLeave={() => setShowSentimentTip(false)}
              >
                <span
                  className={cn(
                    "w-2 h-2 rounded-full flex-shrink-0 cursor-help",
                    segment.sentiment_label === "positive" &&
                      "bg-green-400 dark:bg-green-600",
                    segment.sentiment_label === "negative" &&
                      "bg-red-400 dark:bg-red-600",
                    segment.sentiment_label === "neutral" && "bg-muted"
                  )}
                  aria-label={`Sentiment: ${segment.sentiment_label}${segment.sentiment_confidence != null ? ` (${Math.round(segment.sentiment_confidence * 100)}%)` : ""}${segment.emotion_data?.emotion ? ` — ${segment.emotion_data.emotion}` : ""}`}
                />
                {showSentimentTip && (
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-20 pointer-events-none">
                    <div className="bg-slate-800 text-white text-xs px-3 py-2 rounded-lg shadow-lg whitespace-nowrap">
                      <div className="font-semibold">
                        <span
                          className={cn(
                            segment.sentiment_label === "positive" &&
                              "text-green-300 dark:text-green-700",
                            segment.sentiment_label === "negative" &&
                              "text-red-300 dark:text-red-700",
                            segment.sentiment_label === "neutral" &&
                              "text-muted-foreground"
                          )}
                        >
                          {segment.sentiment_label.charAt(0).toUpperCase() +
                            segment.sentiment_label.slice(1)}
                        </span>
                        {segment.sentiment_confidence != null && (
                          <span className="text-slate-400 font-normal ml-1">
                            ({Math.round(segment.sentiment_confidence * 100)}%)
                          </span>
                        )}
                      </div>
                      {Boolean(segment.emotion_data?.emotion) && (
                        <div className="text-slate-300 text-[10px] mt-0.5">
                          {String(segment.emotion_data?.emotion)}
                        </div>
                      )}
                      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Key Moment Label */}
            {keyMomentType === "tension" && (
              <span className="text-[9px] font-semibold text-red-600 dark:text-red-400 bg-red-500/10 dark:bg-red-600/10 px-1.5 py-0.5 rounded-full">
                Tension
              </span>
            )}
            {keyMomentType === "positive" && (
              <span className="text-[9px] font-semibold text-green-600 dark:text-green-400 bg-green-500/10 dark:bg-green-600/10 px-1.5 py-0.5 rounded-full">
                Connection
              </span>
            )}

            {/* Annotation badges with tooltips */}
            {annotations && annotations.length > 0 && (
              <div className="flex gap-1.5">
                {annotations.map((annotation) => {
                  const isHoveredAnnotation =
                    hoveredAnnotationId === annotation.id;
                  const isPositive = isPositiveAnnotation(
                    annotation.annotation_type
                  );
                  const colors =
                    ANNOTATION_TYPE_COLORS[annotation.annotation_type];
                  const label =
                    ANNOTATION_TYPE_LABELS[annotation.annotation_type];

                  return (
                    <div
                      key={annotation.id}
                      className="relative"
                      onMouseEnter={() => setHoveredAnnotationId(annotation.id)}
                      onMouseLeave={() => setHoveredAnnotationId(null)}
                    >
                      <span
                        className={cn(
                          "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border cursor-help",
                          colors.bg,
                          colors.text,
                          colors.border
                        )}
                      >
                        {isPositive ? (
                          <ThumbsUp className="w-2.5 h-2.5" />
                        ) : (
                          <AlertTriangle className="w-2.5 h-2.5" />
                        )}
                        {label}
                      </span>

                      {/* Tooltip */}
                      {annotation.title && isHoveredAnnotation && (
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-72 z-50 pointer-events-none">
                          <div className="bg-background border border-border rounded-lg shadow-xl p-3">
                            <div className="flex items-start gap-2 mb-2">
                              {isPositive ? (
                                <div className="p-1 rounded-full bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400 flex-shrink-0">
                                  <ThumbsUp className="w-3 h-3" />
                                </div>
                              ) : (
                                <div className="p-1 rounded-full bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-400 flex-shrink-0">
                                  <AlertTriangle className="w-3 h-3" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <h4 className="text-xs font-semibold text-foreground">
                                  {label}
                                </h4>
                                <p className="text-[10px] text-muted-foreground">
                                  Therapist Note
                                </p>
                              </div>
                            </div>
                            <p className="text-xs leading-relaxed text-foreground/90">
                              {annotation.title}
                            </p>
                            {/* Arrow pointer */}
                            <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px">
                              <div className="border-8 border-transparent border-t-border"></div>
                              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-full border-[7px] border-transparent border-t-background"></div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Timestamp - shown on hover, right side */}
            <span
              className={cn(
                "text-xs text-muted-foreground font-mono transition-opacity",
                isHovered ? "opacity-100" : "opacity-0"
              )}
            >
              {formatTimestamp(segment.start_time_ms)}
            </span>

            {/* Chat Button - supports both inline and external chat */}
            {/* Show disabled button with tooltip when speakers not confirmed */}
            {!speakersConfirmed && (
              <div className="relative group/chat">
                <button
                  disabled
                  className={cn(
                    "flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium transition-all cursor-not-allowed",
                    "opacity-0 group-hover:opacity-50 text-muted-foreground"
                  )}
                  aria-label="Confirm speakers to enable chat"
                  type="button"
                >
                  <MessageCircle className="w-3.5 h-3.5" />
                </button>
                {/* Tooltip */}
                <div className="absolute bottom-full right-0 mb-2 hidden group-hover/chat:block z-50 pointer-events-none">
                  <div className="bg-foreground text-background text-xs px-2 py-1 rounded whitespace-nowrap">
                    Confirm speakers first
                  </div>
                </div>
              </div>
            )}
            {speakersConfirmed && (onChatToggle || onCommentClick) && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (onChatToggle) {
                    onChatToggle(isChatOpen ? null : segment.id);
                  } else if (onCommentClick) {
                    onCommentClick(segment.id);
                  }
                }}
                className={cn(
                  "flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium transition-all",
                  hasComments || isChatOpen
                    ? "bg-primary/10 text-primary"
                    : "opacity-0 group-hover:opacity-100 hover:bg-muted/50 text-muted-foreground"
                )}
                aria-label={isChatOpen ? "Close chat" : "Open chat"}
                type="button"
              >
                <MessageCircle className="w-3.5 h-3.5" />
                {hasComments && <span>{chatMessageCount}</span>}
              </button>
            )}
          </div>
        </div>

        {/* Sentences */}
        <div className="leading-relaxed space-y-1">
          {sentences.map((sentence, index) => {
            const isSentenceActive = index === activeSentenceIndex;
            return (
              <span
                key={index}
                onClick={(e) => handleSentenceClick(e, sentence)}
                className={cn(
                  "cursor-pointer transition-all inline rounded px-1 -mx-1",
                  isSentenceActive
                    ? "bg-blue-100 dark:bg-blue-900 text-blue-900 dark:text-blue-100"
                    : isActive
                      ? "text-foreground hover:bg-blue-50 dark:hover:bg-blue-950"
                      : "text-muted-foreground hover:bg-muted/30"
                )}
              >
                {highlightText(sentence.text)}{" "}
              </span>
            );
          })}
        </div>

        {/* Inline Chat */}
        {isChatOpen && transcriptId && (
          <InlineChat
            transcriptId={transcriptId}
            segmentId={segment.id}
            onClose={() => onChatToggle?.(null)}
            onMessageCountChange={handleMessageCountChange}
          />
        )}
      </div>
    </div>
  );
}
